import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion';
import Lenis from 'lenis';
import { Helmet } from 'react-helmet';

import './PortraitsSquare.scss'

import Header from '../components/Header.js';
import { getThumbnailUrl, isVideo, getVideoUrl, getThumbnailDimensions } from '../utils/imageLoader.js';

const Commissions = ({ commissions, buttons }) => {
    const navigate = useNavigate()

    useEffect(() => {
        document.title = "Commissions • Alex de Brabant";
    }, []);

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Smooth/slow scroll only on Commissions page
    useEffect(() => {
        const lenis = new Lenis({
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.8,
        });

        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    // Handle window resize to recalculate text positions
    useEffect(() => {
        const handleResize = () => {
            // Recalculate all text positions after resize
            const images = document.querySelectorAll('.portrait-img-square');
            images.forEach((img, index) => {
                handleImageLoad({ target: img }, index);
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleCommissionClick = (slug) => {
        navigate(`/${slug}`)
    }

    const handleImageLoad = (event, idx) => {
        const img = event.target;
        const container = img.closest('.portrait-img-container-square');
        const textElement = container.querySelector('.portrait-name-square');

        if (img && textElement) {
            const imgWidth = img.offsetWidth;
            textElement.style.width = `${imgWidth}px`;
        }
    }

    const handleVideoMetadata = (event, idx) => {
        const video = event.target;
        if (video && video.videoWidth && video.videoHeight) {
            // Set intrinsic size to stabilize layout and match image behavior
            video.setAttribute('width', video.videoWidth);
            video.setAttribute('height', video.videoHeight);
        }
        // Reuse existing logic to size the title to the rendered media width
        handleImageLoad(event, idx);
    }

    return (

        <AnimatePresence mode="wait">
            <motion.div
                className="portraits-container-square"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
            >
                <Helmet>
                    <title>Commissions • Alex de Brabant</title>
                    <meta name="description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                    <link rel="canonical" href="https://alexdebrabant.com/commissions" />
                    <meta property="og:title" content="Commissions • Alex de Brabant" />
                    <meta property="og:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                    <meta property="og:type" content="website" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content="Commissions • Alex de Brabant" />
                    <meta name="twitter:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                </Helmet>
                <Header pageTitle="Commissions" buttons={buttons} />
                <div className="portraits-grid-square">
                    {commissions.map((commission, idx) => (
                        <div
                            key={idx}
                            className="portrait-card-square"
                            onClick={() => handleCommissionClick(commission.slug)}
                        >
                            <div className="portrait-img-container-square">
                                {commission.mainPicture ? (
                                    isVideo(commission.mainPicture) ? (
                                        <video
                                            className="portrait-img-square"
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            onLoadedMetadata={(e) => handleVideoMetadata(e, idx)}
                                        >
                                            <source src={getVideoUrl(commission.mainPicture)} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    ) : (
                                        <img
                                            src={getThumbnailUrl(commission.mainPicture)}
                                            alt={commission.title}
                                            loading="lazy"
                                            width={getThumbnailDimensions(commission.mainPicture).width}
                                            height={getThumbnailDimensions(commission.mainPicture).height}
                                            className="portrait-img-square"
                                            onLoad={(e) => handleImageLoad(e, idx)}
                                        />
                                    )
                                ) : (
                                    <div className="portrait-img-placeholder">
                                        <span>no image available</span>
                                    </div>
                                )}
                                <div className="portrait-name-square">{commission.title}</div>
                            </div>

                        </div>
                    ))}
                </div>
            </motion.div>
        </AnimatePresence>
    )
}

export default Commissions